<script lang="ts">
  import DashboardLayout from '../../../layout/DashboardLayout.svelte';
  import { authState } from '../../auth/auth.state.svelte';
  import { onMount } from 'svelte';
  import LoadingSpinner from '../../../shared/components/LoadingSpinner.svelte';

  // State reaktif Svelte 5
  let spbList = $state<any[]>([]);
  let loading = $state(true);
  let selectedDivisi = $state('Semua');

  // Daftar divisi yang tersedia untuk filter
  const divisiOptions = ['Semua', 'KYA', 'SKM', 'AWL', 'AWE', 'AWS', 'AWD'];

  // URL Cloudflare Worker
  const API_URL = 'https://falling-mountain-77d7.jrelvin09.workers.dev/api/spb';

  onMount(async () => {
    try {
      const res = await fetch(API_URL);
      if (!res.ok) throw new Error('Network response was not ok');
      spbList = await res.json();
    } catch (err) {
      console.error('Gagal memuat data SPB:', err);
    } finally {
      loading = false;
    }
  });

  // Derived state untuk Filter
  let filteredSpbList = $derived(
    selectedDivisi === 'Semua' 
      ? spbList 
      : spbList.filter((item) => item.divisi === selectedDivisi)
  );

  // Derived state untuk KPI berdasarkan data yang sudah difilter
  let totalRecords = $derived(filteredSpbList.length);
  // Berat Bersih dalam Ton = Kg / 1000
  let totalBersihTon = $derived(
    filteredSpbList.reduce((acc, item) => acc + (item.bersih || 0), 0) / 1000
  );
  let totalJanjang = $derived(
    filteredSpbList.reduce((acc, item) => acc + (item.jml_janjang || 0), 0)
  );
</script>

<DashboardLayout>
  <div class="p-8 max-w-7xl mx-auto">
    <div class="flex items-center justify-between mb-8">
      <h2 class="text-3xl font-bold text-surface-100">Dashboard SPB</h2>
      
      <!-- Filter Divisi -->
      <div class="flex items-center gap-2 bg-surface-800 p-1.5 rounded-xl border border-surface-700">
        {#each divisiOptions as div}
          <button
            class="px-4 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 cursor-pointer
                   {selectedDivisi === div 
                     ? 'bg-brand-600 text-white shadow-md' 
                     : 'text-surface-400 hover:text-surface-200 hover:bg-surface-700'}"
            onclick={() => (selectedDivisi = div)}
          >
            {div}
          </button>
        {/each}
      </div>
    </div>

    {#if loading}
      <div class="h-64 flex flex-col items-center justify-center bg-surface-800 rounded-xl border border-surface-700">
        <LoadingSpinner size="lg" />
        <p class="text-surface-400 mt-4 animate-pulse">Menarik data SPB dari Server...</p>
      </div>
    {:else}
      <!-- Stats Cards Row -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div class="bg-surface-800 rounded-2xl p-6 border border-surface-700 shadow-lg relative overflow-hidden group">
          <div class="absolute -right-6 -top-6 text-brand-500/10 text-8xl transition-transform group-hover:scale-110">📄</div>
          <p class="text-surface-400 text-sm font-medium uppercase tracking-wider relative z-10">Total SPB Masuk</p>
          <p class="text-4xl font-bold text-brand-400 mt-2 relative z-10">{totalRecords.toLocaleString('id-ID')}</p>
        </div>
        
        <div class="bg-surface-800 rounded-2xl p-6 border border-surface-700 shadow-lg relative overflow-hidden group">
          <div class="absolute -right-6 -top-6 text-green-500/10 text-8xl transition-transform group-hover:scale-110">⚖️</div>
          <p class="text-surface-400 text-sm font-medium uppercase tracking-wider relative z-10">Total Berat Bersih</p>
          <p class="text-4xl font-bold text-green-400 mt-2 relative z-10">{totalBersihTon.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span class="text-lg text-green-500/70">Ton</span></p>
        </div>
        
        <div class="bg-surface-800 rounded-2xl p-6 border border-surface-700 shadow-lg relative overflow-hidden group">
          <div class="absolute -right-6 -top-6 text-amber-500/10 text-8xl transition-transform group-hover:scale-110">🌴</div>
          <p class="text-surface-400 text-sm font-medium uppercase tracking-wider relative z-10">Total Janjang</p>
          <p class="text-4xl font-bold text-amber-400 mt-2 relative z-10">{totalJanjang.toLocaleString('id-ID')}</p>
        </div>
      </div>

      <!-- Data Table -->
      <div class="bg-surface-800 rounded-2xl border border-surface-700 shadow-xl overflow-hidden">
        <div class="px-6 py-5 border-b border-surface-700 flex justify-between items-center bg-surface-800/50">
          <h3 class="text-lg font-semibold text-surface-100">Riwayat Timbangan Terakhir</h3>
          <span class="text-xs font-medium px-3 py-1 bg-surface-700 text-surface-300 rounded-full">
            Menampilkan {filteredSpbList.length} data
          </span>
        </div>
        
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left whitespace-nowrap">
            <thead class="text-xs text-surface-400 uppercase bg-surface-900/50 border-b border-surface-700">
              <tr>
                <th class="px-6 py-4 font-semibold tracking-wider">Tanggal</th>
                <th class="px-6 py-4 font-semibold tracking-wider text-center">Divisi</th>
                <th class="px-6 py-4 font-semibold tracking-wider">No. SPB</th>
                <th class="px-6 py-4 font-semibold tracking-wider">Plat Nomor</th>
                <th class="px-6 py-4 font-semibold tracking-wider text-right">Netto (Kg)</th>
                <th class="px-6 py-4 font-semibold tracking-wider text-right">Potongan (Kg)</th>
                <th class="px-6 py-4 font-semibold tracking-wider text-right text-brand-400">Bersih (Kg)</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-surface-700/50 text-surface-200">
              {#if filteredSpbList.length === 0}
                <tr>
                  <td colspan="7" class="px-6 py-12 text-center text-surface-500 italic">
                    Tidak ada data SPB yang ditemukan untuk divisi ini.
                  </td>
                </tr>
              {:else}
                {#each filteredSpbList as spb (spb.id || spb.no_seri_spb || Math.random())}
                  <tr class="hover:bg-surface-700/30 transition-colors">
                    <td class="px-6 py-4">{spb.tgl_terima || '-'}</td>
                    <td class="px-6 py-4 text-center">
                      <span class="px-2.5 py-1 bg-surface-700 text-surface-200 rounded-md text-xs font-bold border border-surface-600">
                        {spb.divisi || '-'}
                      </span>
                    </td>
                    <td class="px-6 py-4 font-medium">{spb.no_seri_spb || '-'}</td>
                    <td class="px-6 py-4">
                      <span class="px-2 py-1 bg-surface-900 rounded font-mono text-xs text-surface-300 border border-surface-700">
                        {spb.plat_nomor || '-'}
                      </span>
                    </td>
                    <td class="px-6 py-4 text-right text-surface-400">{(spb.netto || 0).toLocaleString('id-ID')}</td>
                    <td class="px-6 py-4 text-right text-red-400/80">{(spb.potongan || 0).toLocaleString('id-ID')}</td>
                    <td class="px-6 py-4 text-right font-bold text-brand-400">{(spb.bersih || 0).toLocaleString('id-ID')}</td>
                  </tr>
                {/each}
              {/if}
            </tbody>
          </table>
        </div>
      </div>
    {/if}
  </div>
</DashboardLayout>
